import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";

import SpinnerComponent from "../Components/GenericComponents/SpinnerComponent";
import PrimaryButton from "../Components/GenericComponents/PrimaryButton";
import { useNotify } from "../Helpers/Notifications";
import LocalStorage from "../Helpers/LocalStorage";
import {
  joinConferenceCallApiCall,
  joinConferenceWithTokenApiCall,
} from "../API/conferences/conferencesService";
import { useAuthService } from "../Context/AuthContext";
import { useConferencesService } from "../Context/ConferenceContext";
import { openCall } from "../Helpers/navigationHelper";
import {
  CONFERENCE_ROLE_ATTENDEE,
  CONFERENCE_ROLE_ORGANIZER,
  CONFERENCE_ROLE_PRESENTER,
} from "../Constants/constantValues";
import languageSyncManager from "../Helpers/callHelper/LanguageSyncHelper";
import OnboardConferenceAnonymousUser from "../Components/Conferences/OnboardConferenceAnonymousUser";
import useInAppBrowserEscape from "../hooks/useInAppBrowserEscape";
import InAppBrowserBanner from "../Components/GenericComponents/InAppBrowserBanner";

const ConferenceJoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { notifyError, notifySuccess } = useNotify();
  const { handleLoginUser, handleLogoutUser, updateUser, user, anonUser } = useAuthService();
  const { setCurrentConference, currentConferenceRef } = useConferencesService();

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [requiresSignIn, setRequiresSignIn] = useState(false);
  const [conference, setConference] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const joinRequestRef = useRef(false);
  const { showBanner: showInAppBrowserBanner } = useInAppBrowserEscape();

  const isAttendeeRole = (role) => role === CONFERENCE_ROLE_ATTENDEE;

  const getConferenceRoleForCurrentUser = useCallback(
    (conferenceData, currentUser) => {
      if (!conferenceData || !currentUser) return CONFERENCE_ROLE_ATTENDEE;
      const organizers = conferenceData.organizers || [];
      const participantEntries = conferenceData.participants || [];
      const userId = String(currentUser.id);

      if (organizers.some((org) => String(org.id) === userId)) {
        return CONFERENCE_ROLE_ORGANIZER;
      }

      const roleEntry = participantEntries.find(
        (participant) =>
          participant.user && String(participant.user.id) === userId
      );

      if (roleEntry?.role === CONFERENCE_ROLE_PRESENTER) {
        return CONFERENCE_ROLE_PRESENTER;
      }

      if (roleEntry && isAttendeeRole(roleEntry.role)) {
        return CONFERENCE_ROLE_ATTENDEE;
      }

      return CONFERENCE_ROLE_ATTENDEE;
    },
    []
  );

  const persistConferenceMediaPermissions = useCallback(
    (conferenceData, callDetails, currentUser) => {
      if (!conferenceData || !callDetails) return;
      const settings = conferenceData.settings || {};
      const role = getConferenceRoleForCurrentUser(conferenceData, currentUser);
      const isAttendee = isAttendeeRole(role);
      const micAllowed = isAttendee ? false : true;
      const cameraAllowed = isAttendee ? settings.allow_camera !== false : true;
      const chatAllowed = settings.allow_chat !== false;
      const pollsAllowed = settings.allow_polls !== false;

      LocalStorage.setConferenceMediaPermissions({
        callUuid: callDetails.uuid,
        conferenceId: conferenceData.id,
        role,
        micAllowed,
        cameraAllowed,
        chatAllowed,
        pollsAllowed,
        storedAt: new Date().toISOString(),
      });
    },
    [getConferenceRoleForCurrentUser]
  );

  const startConferenceCall = useCallback(
    async (conferenceData, currentUser = user) => {
      if (!conferenceData?.id) {
        setIsLoading(false);
        setErrorMessage(t("general.something_wrong"));
        return;
      }

      setIsLoading(true);
      joinConferenceCallApiCall(
        conferenceData.id,
        async (response) => {
          const callDetails =
            response?.data?.details ||
            response?.data?.call ||
            response?.data?.conference_call ||
            conferenceData.call;
          if (!callDetails?.uuid) {
            setIsLoading(false);
            setErrorMessage(t("general.something_wrong"));
            return;
          }

          const conferenceWithCall = { ...conferenceData, call: callDetails };
          currentConferenceRef.current = conferenceWithCall;
          setCurrentConference(conferenceWithCall);
          persistConferenceMediaPermissions(
            conferenceData,
            callDetails,
            currentUser
          );
          setIsLoading(false);

          const opened = await openCall(
            navigate,
            callDetails.uuid,
            null,
            false,
            conferenceData.id
          );
          if (!opened) {
            notifyError(t("call_modal.another_call_in_progress"));
          }
        },
        () => {
          setIsLoading(false);
          setErrorMessage(t("general.something_wrong"));
        }
      );
    },
    [
      currentConferenceRef,
      navigate,
      notifyError,
      persistConferenceMediaPermissions,
      setCurrentConference,
      t,
      user,
    ]
  );

  const shouldShowOnboarding = (nextUser) => {
    if (!nextUser) return true;
    const isMissingName = !nextUser.name || nextUser.name.trim().length < 3;
    const isMissingLanguage = !nextUser.language || nextUser.language === "";
    const isMissingCallLanguage =
      !nextUser.call_language || nextUser.call_language === "";
    return isMissingName || isMissingLanguage || isMissingCallLanguage;
  };

  const token = searchParams.get("token");
  const linkToken = searchParams.get("link_token");

  useEffect(() => {
    if (joinRequestRef.current) {
      return;
    }
    joinRequestRef.current = true;

    if (!token && !linkToken) {
      joinRequestRef.current = false;
      setIsLoading(false);
      setErrorMessage(t("general.something_wrong"));
      return;
    }

    joinConferenceWithTokenApiCall(
      token,
      linkToken,
      (response) => {
        const data = response?.data || {};
        const nextUser = data.user;
        const nextConference = data.conference;
        const accessToken = data.token;
        const refreshToken = data.refresh_token;

        if (accessToken) {
          LocalStorage.setAuthenthicationToken(accessToken);
          LocalStorage.setSSOFlag(true);
        }
        if (refreshToken) {
          LocalStorage.setRefreshToken(refreshToken);
        }
        if (nextUser) {
          handleLoginUser(nextUser);
        }

        notifySuccess(t("login.successfull_log_in"));
        setConference(nextConference || null);
        if (shouldShowOnboarding(nextUser)) {
          setIsLoading(false);
          setShowOnboarding(true);
        } else {
          startConferenceCall(nextConference, nextUser);
        }
      },
      (err) => {
        joinRequestRef.current = false;
        setIsLoading(false);

        if (err?.response?.data?.code === "requires_sign_in") {
          setRequiresSignIn(true);
          return;
        }

        const errorMessage = t("conferences.conference_has_ended");
        setErrorMessage(errorMessage);
        notifyError(errorMessage);
      }
    );
  }, [
    token,
    linkToken,
    t,
    notifyError,
    notifySuccess,
    handleLoginUser,
    startConferenceCall,
  ]);

  const goHome = () => {
    if (anonUser.current) {
      handleLogoutUser();
    }
    navigate("/login", { replace: true });
  };

  const goToSignIn = () => {
    if (anonUser.current) {
      handleLogoutUser();
    }
    const joinSearch = token
      ? `token=${encodeURIComponent(token)}`
      : `link_token=${encodeURIComponent(linkToken)}`;
    navigate("/login", {
      replace: true,
      state: { backTo: `/conferences/join?${joinSearch}`, conferenceSignIn: true },
    });
  };

  const handleOnboardingComplete = (values) => {
    const nextConference = conference;
    if (!nextConference) {
      setErrorMessage(t("general.something_wrong"));
      return;
    }

    setIsLoading(true);
    const payload = {
      name: values.name,
      language: values.language,
      call_language: values.callLanguage,
    };

    updateUser(
      payload,
      (response) => {
        const updatedUser = response?.data?.user;
        if (updatedUser) {
          handleLoginUser(updatedUser);
        }
        if (values.callLanguage) {
          if (window.electron?.notifyLanguageChanged) {
            window.electron.notifyLanguageChanged(values.callLanguage);
          }
          languageSyncManager.notifyLanguageChanged(values.callLanguage);
        }
        setShowOnboarding(false);
        startConferenceCall(nextConference, updatedUser || user);
      },
      () => {
        setIsLoading(false);
        notifyError(t("general.something_wrong"));
      }
    );
  };

  if (showOnboarding) {
    return (
      <OnboardConferenceAnonymousUser
        onComplete={handleOnboardingComplete}
        onCancel={goHome}
      />
    );
  }

  return (
    <SpinnerComponent isLoading={isLoading}>
      <Page>
        {showInAppBrowserBanner && <InAppBrowserBanner />}
        <Card>
          {!errorMessage && !requiresSignIn && <Title>{t("general.loading")}</Title>}
          {requiresSignIn ? (
            <>
              <Message>{t("conferences.already_have_account")}</Message>
              <PrimaryButton onClick={goToSignIn} style={{ padding: "12px 16px" }}>
                {t("authentication.password.signin_button")}
              </PrimaryButton>
            </>
          ) : errorMessage ? (
            <>
              <Message>{errorMessage}</Message>
              <PrimaryButton onClick={goHome} style={{ padding: "12px 16px" }}>
                {t("general.ok")}
              </PrimaryButton>
            </>
          ) : (
            <Message>{t("conferences.joining_conference")}</Message>
          )}
        </Card>
      </Page>
    </SpinnerComponent>
  );
};

export default ConferenceJoinPage;

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  padding: 16px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 20px;
  font-weight: 600;
`;

const Message = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  line-height: 20px;
`;
